import { Button, Divider, Form, FormInstance, Modal, Space, Table } from 'antd';
import { useIntl } from 'react-intl';
import { useState } from 'react';
import { v4 } from 'uuid';
import moment from 'moment';
import { DeleteIcon, EditIcon } from '@/components/_common';
import InvoiceDocReferencedModal from '../InvoiceDocReferencedModal';
import { useDisclosure } from '@/hooks';
import { InvoiceGeneralFormValues } from '@/core/domain/invoice/invoice/Invoice.types';
import { getTableScrollX } from '@/utils/table';
import { ColumnsType } from 'antd/lib/table';
import {
  useDeleteInvoiceDocReferenced,
  useInvoicesReferencedDocument,
} from '@/core/domain/invoice/referencedDocument/referencedDocument.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { useRouteMatch } from 'react-router-dom';
import { useInvoiceFormContext } from '../../../InvoiceFormProvider';

type Props = {
  form: FormInstance<InvoiceGeneralFormValues>;
};

function InvoiceDocReferencedTable({ form }: Props) {
  const intl = useIntl().formatMessage;
  const [documentId, setDocumentId] = useState();
  const [modalVisible, modalActions] = useDisclosure();

  const { disabledFields } = useInvoiceFormContext();

  const { invoiceId } = useRouteMatch<{ invoiceId: string }>().params;

  const { data, isLoading } = useInvoicesReferencedDocument(+invoiceId);
  const referencedDocument = flatInfiniteData(data);

  const { mutateAsync: deleteMutateAsync } = useDeleteInvoiceDocReferenced();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnsType<any> = [
    {
      title: intl({
        id: 'invoice.create.referenceDocument.table.type',
      }),
      dataIndex: 'issuerType',
      key: 'issuerType',
      render: (_, row) => {
        if (row.customerId) return 'Cliente';
        if (row.insurerId) return 'Seguradora';
        if (row.supplierId) return 'Fornecedor';
        if (row.key) return 'NF-e';
        return '-';
      },
      width: '6%',
      ellipsis: true,
    },
    {
      title: intl({
        id: 'invoice.create.referenceDocument.table.model',
      }),
      dataIndex: 'model',
      key: 'model',
      render: (value: string) => value || '-',
      width: '6%',
      ellipsis: true,
    },
    {
      title: intl({
        id: 'invoice.create.referenceDocument.table.series',
      }),
      dataIndex: 'serie',
      key: 'serie',
      render: (value: string) => value || '-',
      width: '6%',
      ellipsis: true,
    },
    {
      title: intl({
        id: 'invoice.create.referenceDocument.table.number',
      }),
      dataIndex: 'number',
      key: 'number',
      render: (value: string) => value || '-',
      width: '12%',
      ellipsis: true,
    },
    {
      title: intl({
        id: 'invoice.create.referenceDocument.table.issuer',
      }),
      dataIndex: 'recipientName',
      key: 'recipientName',
      render: (value: string) => value || '-',
      width: '15%',
      ellipsis: true,
    },
    {
      title: intl({
        id: 'invoice.create.referenceDocument.table.acessKey',
      }),
      dataIndex: 'key',
      key: 'key',
      render: (value: string) => value || '-',
      width: '30%',
      ellipsis: true,
    },
    {
      title: intl({
        id: 'invoice.create.referenceDocument.table.issueDate',
      }),
      dataIndex: 'emissionDate',
      key: 'emissionDate',
      render: (value: string) =>
        value ? moment(value).format('DD/MM/YYYY') : '-',
      width: '10%',
      ellipsis: true,
    },
    {
      title: intl({
        id: 'invoice.create.referenceDocument.table.uf',
      }),
      dataIndex: 'uf',
      key: 'uf',
      render: (value: string) => value || '-',
      width: '6%',
      ellipsis: true,
    },
    {
      key: 'operation',
      width: '8%',
      fixed: 'right',
      align: 'right',
      render: (_, row) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => {
                setDocumentId(row.id);
                modalActions.toggle();
              },
            }}
            disabled={disabledFields}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => {
              Modal.confirm({
                title: 'Deseja realmente excluir o Documento Referenciado?',
                content: `Ao remover o Documento Referenciado, a recuperação do mesmo não poderá ser feita. Ainda sim deseja coninuar?`,
                onOk: () => {
                  deleteMutateAsync({
                    invoiceId: Number(invoiceId),
                    id: Number(row.id),
                  });
                },
                okType: 'danger',
              });
            }}
            disabled={disabledFields}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Form form={form} name="invoiceGeneral">
        <Button
          type="primary"
          style={{ float: 'right' }}
          onClick={modalActions.open}
          disabled={disabledFields}
        >
          {intl({
            id: 'layout.menu.fiscal.invoice.newDocument',
          })}
        </Button>

        <Form.List name="docReferenceds" initialValue={[]}>
          {(_fields) => {
            return (
              <>
                <Table
                  dataSource={referencedDocument}
                  columns={columns}
                  pagination={false}
                  loading={isLoading}
                  scroll={{ x: getTableScrollX(columns) }}
                  rowKey={() => v4()}
                  className="table-small"
                />
              </>
            );
          }}
        </Form.List>
      </Form>

      <InvoiceDocReferencedModal
        isEdit={!!documentId}
        visible={modalVisible}
        documentId={documentId}
        onClose={() => {
          modalActions.close();
          setDocumentId(undefined);
        }}
      />
    </>
  );
}

export default InvoiceDocReferencedTable;
